import { bytesMatchAscii, readLatin1Range, readLatin1String, readUInt32LE } from "../buffer-io";
import { parseXrkBuffer } from "./parse";
import {
  detectCorruptDownload,
  isZlibWrapped,
  loadTelemetryBuffer,
  readTelemetryBuffer,
  validateTelemetryBuffer,
} from "./io";

export type AimSessionMetadata = {
  logDate?: string;
  logTime?: string;
  driver?: string;
  vehicle?: string;
  venue?: string;
};

export type XrkMetadata = {
  path: string;
  format: "xrk" | "xrz" | "drk" | "unknown";
  sizeBytes: number;
  channels: string[];
  footer: Record<string, string>;
  session?: AimSessionMetadata;
};

const SECTION_TAGS = ["<hCNF", "<hCHS", "<hFOT", "<hDAT"] as const;

export {
  AIM_DOWNLOAD_CHUNK_BYTES,
  detectCorruptDownload,
  detectDuplicateDownloadChunks,
  isZlibWrapped,
  loadTelemetryBuffer,
  readTelemetryBuffer,
  validateTelemetryBuffer,
} from "./io";

export function loadTelemetryFile(buffer: Buffer): Buffer {
  return readTelemetryBuffer(buffer);
}

export function inspectTelemetryBuffer(buffer: Buffer, label = "buffer"): XrkMetadata {
  const raw = buffer;
  const corruptReason = detectCorruptDownload(raw);
  if (corruptReason) {
    return {
      path: label,
      format: "unknown",
      sizeBytes: raw.length,
      channels: [],
      footer: { error: corruptReason },
    };
  }

  const decoded = loadTelemetryBuffer(raw);
  const format = detectFormat(raw, decoded);
  const parsed = parseXrkBuffer(raw, { headersOnly: true });

  const session = parseSessionMetadata(decoded);

  return {
    path: label,
    format,
    sizeBytes: decoded.length,
    channels: parsed.channels.map((channel) => channel.shortName),
    footer: parseFooter(decoded),
    session,
  };
}

export function parseAimHeaderString(buffer: Buffer, token: string): string | null {
  const tag = Buffer.from(`<h${token}`, "latin1");
  const offset = buffer.indexOf(tag);
  if (offset < 0) {
    return null;
  }

  const payloadLength = readUInt32LE(buffer, offset + 6);
  if (payloadLength <= 0 || payloadLength > 512) {
    return null;
  }

  const payloadStart = offset + 12;
  if (payloadStart + payloadLength > buffer.length) {
    return null;
  }

  const value = readLatin1String(buffer, payloadStart, payloadLength);

  return value || null;
}

export function parseSessionMetadata(buffer: Buffer): AimSessionMetadata {
  return {
    logDate: parseAimHeaderString(buffer, "TMD") ?? undefined,
    logTime: parseAimHeaderString(buffer, "TMT") ?? undefined,
    driver: parseAimHeaderString(buffer, "RCR") ?? undefined,
    vehicle: parseAimHeaderString(buffer, "VEH") ?? undefined,
    venue: parseAimTrackName(buffer) ?? undefined,
  };
}

function parseAimTrackName(buffer: Buffer): string | null {
  const tag = Buffer.from("<hTRK", "latin1");
  const offset = buffer.indexOf(tag);
  if (offset < 0) {
    return null;
  }

  const payloadLength = readUInt32LE(buffer, offset + 6);
  if (payloadLength <= 0 || payloadLength > 512) {
    return null;
  }

  const payloadStart = offset + 12;
  const payloadEnd = payloadStart + payloadLength;
  if (payloadEnd > buffer.length) {
    return null;
  }

  let end = payloadStart;
  while (end < payloadEnd) {
    const byte = buffer[end]!;
    if (byte === 0 || byte < 0x20 || byte > 0x7e) {
      break;
    }
    end += 1;
  }

  const value = readLatin1String(buffer, payloadStart, end - payloadStart);
  return value || null;
}

/** AIM footer dates use MM/DD/YYYY and HH:MM[:SS] in logger-local time. */
export function aimLogDateTimeToMs(logDate: string, logTime?: string | null): number | null {
  const dateMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(logDate.trim());
  if (!dateMatch) {
    return null;
  }

  const month = Number(dateMatch[1]);
  const day = Number(dateMatch[2]);
  const year = Number(dateMatch[3]);
  const timeMatch = logTime ? /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(logTime.trim()) : null;
  const hour = timeMatch ? Number(timeMatch[1]) : 12;
  const minute = timeMatch ? Number(timeMatch[2]) : 0;
  const second = timeMatch?.[3] ? Number(timeMatch[3]) : 0;
  const recordedAtMs = new Date(year, month - 1, day, hour, minute, second).getTime();

  return Number.isNaN(recordedAtMs) ? null : recordedAtMs;
}

function detectFormat(raw: Buffer, decoded: Buffer): XrkMetadata["format"] {
  if (isZlibWrapped(raw)) {
    return "xrz";
  }
  if (bytesMatchAscii(decoded, 0, "<hCNF")) {
    return "xrk";
  }
  if (bytesMatchAscii(decoded, 0, "<hDRK")) {
    return "drk";
  }
  return "unknown";
}

function parseFooter(buffer: Buffer): Record<string, string> {
  const footerOffset = buffer.indexOf("<hFOT");
  if (footerOffset < 0) {
    return {};
  }

  const sectionLength = readUInt32LE(buffer, footerOffset + 6);
  const section = buffer.subarray(footerOffset, footerOffset + sectionLength + 10);
  const text = readLatin1Range(section, 0, section.length);

  const fields: Record<string, string> = {};
  const patterns: Array<[key: string, regex: RegExp]> = [
    ["vehicle", /Vehicle[^\0]{0,8}([\x20-\x7E]{1,40})/],
    ["driver", /Racer[^\0]{0,8}([\x20-\x7E]{1,40})/],
    ["track", /Track[^\0]{0,8}([\x20-\x7E]{1,40})/],
    ["championship", /Championship[^\0]{0,8}([\x20-\x7E]{1,40})/],
    ["venue", /Venue[^\0]{0,8}([\x20-\x7E]{1,40})/],
  ];

  for (const [key, regex] of patterns) {
    const match = regex.exec(text);
    if (match?.[1]) {
      fields[key] = match[1].trim();
    }
  }

  return fields;
}

export function isLikelyAimFile(buffer: Buffer): boolean {
  return SECTION_TAGS.some((tag) => bytesMatchAscii(buffer, 0, tag)) || isZlibWrapped(buffer);
}
